import { observer } from 'mobx-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useStore } from '../../store/store';
import { IUser } from '../../store/adminStore';
import { DataGrid } from '@mui/x-data-grid/DataGrid/DataGrid';
import Avatar from '@mui/material/Avatar/Avatar';
import { GridColDef } from '@mui/x-data-grid/models/colDef/gridColDef';
import Box from '@mui/material/Box/Box';
import CircularLoading from '../../components/loading/circularLoading';
import Button from '@mui/material/Button/Button';
import { useSnackbar } from 'notistack';
import moment from 'moment';
import Typography from '@mui/material/Typography/Typography';
import useTheme from '@mui/material/styles/useTheme';
import TextField from '@mui/material/TextField/TextField';

interface IRowUser extends IUser {
  id: string;
}

const AdminUsersTab: React.FC = observer(() => {
  const { adminStore } = useStore();
  const { getAllUsers, setTestOwner } = adminStore;
  const theme = useTheme();
  const [rows, setRows] = useState<IRowUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    getAllUsers().then((aUserList) => {
      const tempRows = aUserList.map((user) => ({
        id: user.user_id,
        ...user,
      }));
      setRows(tempRows);
      setLoading(false);
    });
  }, []);

  const columns: GridColDef[] = [
    {
      field: 'picture',
      headerName: 'Avatar',
      width: 100,
      renderCell: (params) => <Avatar alt='user' src={params.value} />,
      cellClassName: 'no-select',
    },
    { field: 'name', headerName: 'Name', width: 150 },
    { field: 'email', headerName: 'Email', width: 200 },
    {
      field: 'created_at',
      headerName: 'Created At',
      width: 150,
      valueFormatter: (params: any) => moment(params.value).format('DD.MM.YYYY HH:mm'),
    },
    {
      field: 'last_login',
      headerName: 'Last Login',
      width: 180,
      valueFormatter: (params: any) => moment(params.value).format('DD.MM.YYYY HH:mm'),
    },
    { field: 'logins_count', headerName: 'Logins', width: 100, type: 'number' },
    {
      field: 'roles',
      headerName: 'Roles',
      width: 120,
      renderCell: (params) => (Array.isArray(params.value) ? params.value.join(', ') : 'no role'),
    },
    {
      field: 'actions',
      headerName: 'actions',
      width: 170,
      renderCell: (params) => (
        <Button variant='outlined' onClick={() => setTestOwner(params.row.id)} style={{ padding: '6px 12px', cursor: 'pointer' }}>
          Set Test Owner
        </Button>
      ),
      cellClassName: 'no-select',
    },
  ];

  const filteredRows = useMemo(() => {
    return rows.filter((row) => `${row.name} ${row.email} ${row.roles?.join(' ') ?? ''}`.toLowerCase().includes(search.toLowerCase()));
  }, [rows, search]);

  if (loading) {
    return (
      <>
        <CircularLoading loadingText='Henter alle brukere...' />
      </>
    );
  }

  return (
    <div style={{ flex: '1 1 auto', display: 'grid', placeContent: 'center' }}>
      <Box sx={{ height: 800, width: '1200px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'end' }}>
          <Typography variant='subtitle2' sx={{ color: theme.palette.gray.main }}>
            Double click a cell to copy its content.
          </Typography>
          <TextField label='Search...' variant='standard' value={search} onChange={(e) => setSearch(e.target.value)} sx={{ marginBottom: 2 }} />
        </div>
        {
          <DataGrid
            onCellDoubleClick={(params) => {
              if (params.colDef.cellClassName === 'no-select') return;
              const cellValue = params.formattedValue;
              if (typeof cellValue === 'string' || typeof cellValue === 'number') {
                navigator.clipboard.writeText(String(cellValue)).then(() => {
                  enqueueSnackbar(`Copied cell value: ${cellValue}`, { variant: 'info' });
                });
              }
            }}
            disableColumnSelector
            disableSelectionOnClick
            checkboxSelection={false}
            rows={filteredRows}
            columns={columns}
            sx={{
              '& .no-select': {
                userSelect: 'none',
                color: '#999',
                otuline: 0,
              },
              '& .no-select:focus': {
                outline: 'none',
              },
            }}
          />
        }
      </Box>
    </div>
  );
});

export default AdminUsersTab;
